import { STAGE, fmtInt, ageTone } from "@/lib/format";
import { UsersThree, Lightning } from "@phosphor-icons/react";

const OTHER = "__OTHER__";

export const BuyerTiles = ({ leaderboard, activeBuyer, onSelectBuyer }) => {
  const data = [...(leaderboard || [])].sort((a, b) => (a.other ? 1 : 0) - (b.other ? 1 : 0) || b.count - a.count);
  const total = data.reduce((s, d) => s + d.count, 0) || 1;

  return (
    <div className="col-span-12 border border-[#E3E7EC] bg-[#FFFFFF] p-5" data-testid="buyer-tiles">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <UsersThree size={18} weight="duotone" className="text-[#F0323E]" />
          <div>
            <h3 className="text-sm font-semibold text-[#0F172A]">Open Lines by Buyer</h3>
            <p className="pf-label mt-0.5">Unprocessed line items in this snapshot · sums to total · click to filter</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 rounded-sm border border-[#E3E7EC] bg-[#F9FAFB] px-3 py-2 font-mono text-[10px]" data-testid="buyer-tiles-legend">
          <span className="pf-label !text-[10px] text-[#0F172A]">Key:</span>
          <span className="flex items-center gap-1 text-[#64748B]"><b className="text-[#0F172A]">123</b> open lines</span>
          <span className="flex items-center gap-1 text-[#F0323E]">+/−<span className="text-[#64748B]"> net vs prev day</span></span>
          <span className="flex items-center gap-1 text-[#F0323E]">↑ new<span className="text-[#64748B]"> assigned</span></span>
          <span className="flex items-center gap-1 text-emerald-600">↓ cleared<span className="text-[#64748B]"> OQ→OF</span></span>
          {["OR", "OQ", "OF"].map((k) => (
            <span key={k} className="flex items-center gap-1 text-[#64748B]">
              <span className="inline-block h-2 w-2" style={{ background: STAGE[k].color }} /> {k}
            </span>
          ))}
          <span className="flex items-center gap-1 text-[#64748B]"><b className="text-[#0F172A]">Xd</b> avg age</span>
          <span className="flex items-center gap-1 rounded-sm bg-[#FDE2E4] px-1 font-bold text-[#B91C1C]"><Lightning size={10} weight="fill" />N<span className="font-normal"> production drivers</span></span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-px bg-[#E3E7EC] sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        {data.map((d) => {
          const isOther = !!d.other;
          const filterValue = isOther ? OTHER : d.buyer;
          const active = activeBuyer === filterValue;
          return (
            <button
              key={d.buyer}
              data-testid={`buyer-tile-${d.buyer}`}
              onClick={() => onSelectBuyer(active ? "" : filterValue)}
              className={`group relative flex flex-col justify-between p-4 text-left transition-colors ${
                active ? "bg-[#F0323E]/10" : isOther ? "bg-[#F9FAFB] hover:bg-[#EDEFF3]" : "bg-white hover:bg-[#EDEFF3]"
              }`}
            >
              <div className={`truncate text-xs font-medium ${isOther && !active ? "text-[#9CA3AF]" : "text-[#64748B]"}`} title={d.buyer}>{d.buyer}</div>
              <div className="mt-2 flex items-baseline gap-1.5">
                <span className={`font-mono text-2xl font-semibold tracking-tight ${active ? "text-[#F0323E]" : isOther ? "text-[#9CA3AF]" : "text-[#0F172A]"}`}>
                  {fmtInt(d.count)}
                </span>
                <span className="pf-label">lines</span>
                {d.critical_count > 0 && (
                  <span
                    data-testid={`buyer-critical-${d.buyer}`}
                    title="Production Driver lines"
                    className="inline-flex items-center gap-0.5 rounded-sm bg-[#FDE2E4] px-1 font-mono text-[10px] font-bold text-[#B91C1C]"
                  >
                    <Lightning size={10} weight="fill" />{fmtInt(d.critical_count)}
                  </span>
                )}
                {typeof d.count_delta === "number" && (
                  <span
                    data-testid={`buyer-delta-${d.buyer}`}
                    title="vs previous day"
                    className={`ml-auto font-mono text-[11px] font-semibold ${
                      d.count_delta > 0 ? "text-[#F0323E]" : d.count_delta < 0 ? "text-[#16A34A]" : "text-[#9CA3AF]"
                    }`}
                  >
                    {d.count_delta > 0 ? "+" : d.count_delta < 0 ? "−" : "±"}{fmtInt(Math.abs(d.count_delta))}
                  </span>
                )}
              </div>
              {(typeof d.new_lines === "number" || typeof d.cleared_lines === "number") && (
                <div className="mt-1 flex items-center gap-2 font-mono text-[10px]" data-testid={`buyer-churn-${d.buyer}`}>
                  {d.new_lines > 0 && <span className="text-[#F0323E]">↑{fmtInt(d.new_lines)} new</span>}
                  {d.cleared_lines > 0 && <span className="text-emerald-600">↓{fmtInt(d.cleared_lines)} cleared</span>}
                  {!d.new_lines && !d.cleared_lines && <span className="text-[#9CA3AF]">no line changes</span>}
                </div>
              )}
              <div className="mt-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  {Object.keys(STAGE).map((k) =>
                    d[`${k}_count`] ? (
                      <span key={k} className="flex items-center gap-1 font-mono text-[10px] text-[#64748B]">
                        <span className="inline-block h-2 w-2" style={{ background: STAGE[k].color }} />
                        {fmtInt(d[`${k}_count`])}
                      </span>
                    ) : null
                  )}
                </div>
                {!isOther && <span className={`font-mono text-[11px] font-semibold ${ageTone(d.avg_age)}`}>{fmtInt(d.avg_age)}d</span>}
              </div>
              <div className="mt-2 h-1 overflow-hidden rounded-full bg-[#F1F3F6]">
                <div className={`h-full rounded-full ${isOther && !active ? "bg-[#CAD1DB]" : "bg-[#F0323E]/70"}`} style={{ width: `${(d.count / total) * 100}%` }} />
              </div>
            </button>
          );
        })}
        {data.length === 0 && <p className="col-span-full bg-white py-6 text-center text-sm text-[#9CA3AF]">No buyers</p>}
      </div>
    </div>
  );
};
